import IS_TOUCH_SUPPORTED from '@environment/touchSupport';
import EventListenerBase from '@helpers/eventListenerBase';
import ListenerSetter from '@helpers/listenerSetter';
import safeAssign from '@helpers/object/safeAssign';
import findUpClassName from '@helpers/dom/findUpClassName';
import Modes from '@config/modes';

export default class ControlsHover extends EventListenerBase<{
  toggleControls: (show: boolean) => void
}> {
  protected hideControlsTimeout: number;
  protected controlsLocked: boolean;

  protected canHideControls: () => boolean;
  protected canShowControls: () => boolean;
  protected element: HTMLElement;
  protected listenerSetter: ListenerSetter;
  protected showOnLeaveToClassName: string | string[];
  protected ignoreClickClassName: string;

  constructor() {
    super(false);
    this.hideControlsTimeout = 0;
  }

  public setup(options: {
    element: HTMLElement,
    listenerSetter: ListenerSetter,
    canHideControls?: () => boolean,
    canShowControls?: () => boolean,
    showOnLeaveToClassName?: string | string[],
    ignoreClickClassName?: string
  }) {
    safeAssign(this, options);

    const {listenerSetter, element} = this;
    if(Modes.a11y) {
      listenerSetter.add(element.ownerDocument)('focusin', () => {
        if(this.hasKeyboardFocus()) this.showControls(false);
      });
      listenerSetter.add(element.ownerDocument)('focusout', () => {
        if(element.classList.contains('show-controls')) this.hideControls(true);
      });
    }
    listenerSetter.addCleanup(() => clearTimeout(this.hideControlsTimeout));

    if(IS_TOUCH_SUPPORTED) {
      listenerSetter.add(element)('click', (e) => {
        if(this.ignoreClickClassName && findUpClassName(e.target, this.ignoreClickClassName)) {
          return;
        }

        this.toggleControls();
      });

      /* listenerSetter.add(player)('touchstart', () => {
        showControls(false);
      });

      listenerSetter.add(player)('touchend', () => {
        if(player.classList.contains('is-playing')) {
          showControls();
        }
      }); */
    } else {
      listenerSetter.add(element)('mousemove', () => {
        this.showControls();
      });

      listenerSetter.add(element)('mouseenter', () => {
        this.showControls(false);
      });

      listenerSetter.add(element)('mouseleave', (e) => {
        // Leaving onto floating chrome (caption / topbar) must not start the hide —
        // those elements live outside the player, so hiding here would yank them from
        // under the cursor and bounce mouseenter↔mouseleave forever.
        const showOnLeaveClassNames = Array.isArray(this.showOnLeaveToClassName) ? this.showOnLeaveToClassName : [this.showOnLeaveToClassName];
        if(e.relatedTarget && showOnLeaveClassNames.some((className) => className && findUpClassName(e.relatedTarget, className))) {
          this.showControls(false);
          return;
        }

        this.hideControls();
      });
    }
  }

  public hideControls = (setHideTimeout = false) => {
    if(setHideTimeout) {
      if(!this.hideControlsTimeout) {
        this.hideControlsTimeout = window.setTimeout(this.hideControls, 3e3);
      }

      return;
    }

    clearTimeout(this.hideControlsTimeout);
    this.hideControlsTimeout = 0;
    if(this.controlsLocked !== false && this.hasKeyboardFocus()) return;

    const isShown = this.element.classList.contains('show-controls');
    if(this.controlsLocked !== false) {
      if((this.canHideControls ? !this.canHideControls() : false) || !isShown || this.controlsLocked) {
        return;
      }
    } else if(!isShown) {
      return;
    }

    this.dispatchEvent('toggleControls', false);
    this.element.classList.remove('show-controls');
  };

  private hasKeyboardFocus() {
    if(!Modes.a11y) return false;
    const active = this.element.ownerDocument.activeElement as HTMLElement;
    if(!active?.matches(':focus-visible')) return false;
    const controls = Array.isArray(this.showOnLeaveToClassName) ? this.showOnLeaveToClassName : [this.showOnLeaveToClassName];
    return this.element.contains(active) || controls.some((className) => className && !!findUpClassName(active, className));
  }

  public showControls = (setHideTimeout = true) => {
    if(!(this.canShowControls?.() ?? true)) return;

    if(this.hideControlsTimeout) {
      clearTimeout(this.hideControlsTimeout);
      this.hideControlsTimeout = 0;
    } else if(!this.element.classList.contains('show-controls') && this.controlsLocked !== false) {
      this.dispatchEvent('toggleControls', true);
      this.element.classList.add('show-controls');
    }

    if(!setHideTimeout || this.controlsLocked) {
      return;
    }

    this.hideControlsTimeout = window.setTimeout(this.hideControls, 3e3);
  };

  public toggleControls = (show?: boolean) => {
    const isShown = this.element.classList.contains('show-controls');

    if(show === undefined) {
      if(isShown) this.hideControls();
      else this.showControls();
    } else if(show === isShown) return;
    else if(show === false) this.hideControls();
    else this.showControls();
  };

  public lockControls(visible: boolean) {
    if(this.controlsLocked === visible) {
      return;
    }

    this.controlsLocked = visible;
    this.element.classList.toggle('disable-hover', visible === false);
    this.toggleControls(visible);
  }
}
